import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api } from '../api/client';
import type { IngestResponse } from '../types';
import IngestCandidateCard from '../components/IngestCandidateCard';

// The ingestion surface (the input-side language boundary). Read a source -- paste, a link, or an
// uploaded text file -- and the model proposes which registry signals it reports, with the value,
// unit, as-of date, and the verbatim quote each figure traces to. Every proposal is a staged draft;
// nothing is recorded until the operator confirms, and confirm routes through the same deterministic
// engine as a manual reading. A link is fetched server-side, SSRF-bounded. With the flag off or no
// key the surface explains the feature needs a model and that numeric injection remains; it does not
// error.

const SAMPLE_TEXT =
  'Nielsen scan data shows hero-can sales fell 9 percent last week while the shelf price held at 99 cents. ' +
  'Separately, aluminium futures rose on fresh tariff news.';
const SAMPLE_REF = 'Nielsen weekly scan, week 23';

type Mode = 'paste' | 'link' | 'file';

export default function Ingest() {
  const [mode, setMode] = useState<Mode>('paste');
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [ref, setRef] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<IngestResponse | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const run = useMutation({
    mutationFn: () => {
      const sourceRef = ref.trim() || undefined;
      if (mode === 'file' && file) return api.ingestUpload(file, sourceRef ?? file.name);
      if (mode === 'link') return api.ingest({ source_url: url.trim(), source_ref: sourceRef });
      return api.ingest({ source_text: text, source_ref: sourceRef });
    },
    onSuccess: (res) => setResult(res),
  });

  const canSubmit = mode === 'paste' ? text.trim() !== '' : mode === 'link' ? url.trim() !== '' : file !== null;

  return (
    <div>
      <div className="ph">Strategy &amp; Pipeline &middot; Text Ingestion</div>
      <div className="h2">Read a signal from a source</div>
      <p className="lead">
        Paste a document, link a page, or upload a text file, and the system proposes which registry
        signals it reports, with the value and the exact quote each figure comes from. Nothing is
        recorded until you confirm; on confirm the value routes through the same deterministic engine as
        a manual reading. Every proposal is a draft you decide on.
      </p>

      <div className="panel" style={{ marginBottom: 14 }}>
        <div className="row" style={{ gap: 6, marginBottom: 12 }}>
          {(['paste', 'link', 'file'] as Mode[]).map((m) => (
            <button
              key={m}
              type="button"
              className={`btn ${mode === m ? 'primary' : 'ghost'}`}
              style={{ minHeight: 'auto', padding: '5px 12px', fontSize: 13 }}
              onClick={() => { setMode(m); setResult(null); }}
            >
              {m === 'paste' ? 'Paste text' : m === 'link' ? 'Link' : 'Upload'}
            </button>
          ))}
        </div>

        <label className="small faint" htmlFor="ingest-ref" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
          Source reference (title, origin, or date)
        </label>
        <input id="ingest-ref" className="c2-input" style={{ width: '100%', marginBottom: 10 }} placeholder="e.g. Nielsen weekly scan, week 23" value={ref} onChange={(e) => setRef(e.target.value)} aria-label="Source reference" />

        {mode === 'paste' && (
          <textarea
            id="ingest-text"
            className="c2-input"
            style={{ width: '100%', minHeight: 130, fontFamily: 'inherit', resize: 'vertical' }}
            placeholder="Paste a press release, supplier note, scan-data summary, or report&hellip;"
            value={text}
            onChange={(e) => setText(e.target.value)}
            aria-label="Pasted text"
          />
        )}
        {mode === 'link' && (
          <input id="ingest-url" className="c2-input" style={{ width: '100%' }} placeholder="https://example.com/article" value={url} onChange={(e) => setUrl(e.target.value)} aria-label="Source URL" />
        )}
        {mode === 'file' && (
          <div>
            <input ref={fileRef} type="file" accept=".txt,text/plain" style={{ display: 'none' }} onChange={(e) => setFile(e.target.files?.[0] ?? null)} aria-label="Upload a text file" />
            <button type="button" className="btn ghost" onClick={() => fileRef.current?.click()}>Choose a text file</button>
            {file && <span className="small faint" style={{ marginLeft: 10, fontSize: 13 }}>{file.name}</span>}
          </div>
        )}

        <div className="row" style={{ gap: 10, marginTop: 12 }}>
          <button type="button" className="btn primary" onClick={() => run.mutate()} disabled={run.isPending || !canSubmit}>
            {run.isPending ? 'Reading…' : 'Extract candidates'}
          </button>
          {mode === 'paste' && (
            <button type="button" className="btn ghost" onClick={() => { setText(SAMPLE_TEXT); setRef(SAMPLE_REF); }}>Load a sample</button>
          )}
        </div>
        {run.isError && <div className="login-err" style={{ marginTop: 8 }}>Could not reach the system. Try again.</div>}
      </div>

      {result?.message && (
        <div className="panel">
          <div className="small">{result.message}</div>
          {!result.available && (
            <div className="small faint" style={{ marginTop: 6 }}>
              Numeric injection on the Signal &amp; Data Registry remains available and is unchanged.
            </div>
          )}
        </div>
      )}

      {result && result.available && result.candidates.length === 0 && !result.message && (
        <div className="panel"><div className="small faint">No registry signal was reported in that source. Nothing staged.</div></div>
      )}

      {result && result.candidates.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="small faint">
            {result.candidates.length} candidate{result.candidates.length > 1 ? 's' : ''} staged. Each is a draft; confirm or reject.
          </div>
          {result.candidates.map((c) => (
            <IngestCandidateCard key={c.id} candidate={c} />
          ))}
        </div>
      )}
    </div>
  );
}
